import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  adminListFaqs,
  adminUpsertFaq,
  adminDeleteFaq,
  type FaqRow,
} from "@/lib/category-faqs.functions";

const SLUGS = [
  "verlichting",
  "groepenkasten",
  "beveiliging",
  "netwerken",
] as const;

const SLUG_LABEL: Record<string, string> = {
  verlichting: "LED Verlichting",
  groepenkasten: "Groepenkasten",
  beveiliging: "Beveiliging",
  netwerken: "Netwerken",
};

export const Route = createFileRoute("/_authenticated/admin/faqs")({
  head: () => ({
    meta: [
      { title: "Admin — FAQ beheer" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminFaqsPage,
});

type Draft = {
  id?: string;
  category_slug: (typeof SLUGS)[number];
  question: string;
  answer: string;
  sort_order: number;
  is_published: boolean;
};

const emptyDraft = (slug: (typeof SLUGS)[number]): Draft => ({
  category_slug: slug,
  question: "",
  answer: "",
  sort_order: 0,
  is_published: true,
});

function AdminFaqsPage() {
  const { isAdmin, loading } = useAuth();
  const list = useServerFn(adminListFaqs);
  const upsert = useServerFn(adminUpsertFaq);
  const del = useServerFn(adminDeleteFaq);
  const qc = useQueryClient();

  const [activeSlug, setActiveSlug] =
    useState<(typeof SLUGS)[number]>("verlichting");
  const [draft, setDraft] = useState<Draft>(emptyDraft("verlichting"));

  const { data, isLoading } = useQuery({
    queryKey: ["admin-faqs"],
    queryFn: () => list({}),
    enabled: isAdmin,
  });

  const grouped = useMemo(() => {
    const map: Record<string, FaqRow[]> = {};
    (data?.faqs ?? []).forEach((f) => {
      (map[f.category_slug] ??= []).push(f);
    });
    return map;
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (input: Draft) => upsert({ data: input }),
    onSuccess: () => {
      toast.success("Opgeslagen");
      qc.invalidateQueries({ queryKey: ["admin-faqs"] });
      setDraft(emptyDraft(activeSlug));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Verwijderd");
      qc.invalidateQueries({ queryKey: ["admin-faqs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <p>Laden…</p>;
  if (!isAdmin) return <p className="text-[#3a7bd5]">Geen toegang.</p>;

  const rows = grouped[activeSlug] ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Veelgestelde vragen beheer</h1>
        <p className="text-sm text-muted-foreground">
          Alle FAQ's worden geladen vanuit de database en getoond op de
          info-pagina's (bv. /info/verlichting).
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {SLUGS.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={s === activeSlug ? "default" : "outline"}
            onClick={() => {
              setActiveSlug(s);
              setDraft(emptyDraft(s));
            }}
          >
            {SLUG_LABEL[s]} ({grouped[s]?.length ?? 0})
          </Button>
        ))}
      </div>

      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-lg font-semibold mb-3">
          {draft.id ? "FAQ bewerken" : "Nieuwe FAQ"} — {SLUG_LABEL[activeSlug]}
        </h2>
        <div className="grid gap-3">
          <div>
            <Label>Vraag</Label>
            <Input
              value={draft.question}
              onChange={(e) =>
                setDraft({ ...draft, question: e.target.value })
              }
              placeholder="Wat betekent lumen?"
            />
          </div>
          <div>
            <Label>Antwoord</Label>
            <Textarea
              rows={5}
              value={draft.answer}
              onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Label>Volgorde</Label>
              <Input
                type="number"
                className="w-24"
                value={draft.sort_order}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    sort_order: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={draft.is_published}
                onChange={(e) =>
                  setDraft({ ...draft, is_published: e.target.checked })
                }
              />
              Gepubliceerd
            </label>
            <div className="ml-auto flex gap-2 mt-6">
              {draft.id && (
                <Button
                  variant="outline"
                  onClick={() => setDraft(emptyDraft(activeSlug))}
                >
                  Annuleren
                </Button>
              )}
              <Button
                disabled={
                  !draft.question.trim() ||
                  !draft.answer.trim() ||
                  saveMut.isPending
                }
                onClick={() =>
                  saveMut.mutate({ ...draft, category_slug: activeSlug })
                }
              >
                {draft.id ? "Bijwerken" : "Toevoegen"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {SLUG_LABEL[activeSlug]} — {rows.length} FAQ's
        </h2>
        {isLoading && <p>Laden…</p>}
        {rows.map((f) => (
          <div
            key={f.id}
            className="rounded-lg border bg-card p-4 flex flex-wrap items-start justify-between gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">
                  #{f.sort_order}
                </span>
                {!f.is_published && (
                  <span className="text-xs rounded bg-yellow-200 px-1.5 text-yellow-900">
                    concept
                  </span>
                )}
                <span className="font-semibold">{f.question}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                {f.answer}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setDraft({
                    id: f.id,
                    category_slug: f.category_slug as (typeof SLUGS)[number],
                    question: f.question,
                    answer: f.answer,
                    sort_order: f.sort_order,
                    is_published: f.is_published,
                  })
                }
              >
                Bewerken
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (confirm("Verwijder deze FAQ?")) deleteMut.mutate(f.id);
                }}
              >
                Verwijderen
              </Button>
            </div>
          </div>
        ))}
        {!isLoading && rows.length === 0 && (
          <p className="text-muted-foreground">Nog geen FAQ's.</p>
        )}
      </div>
    </div>
  );
}
